import { useCallback } from 'react';

import { fg } from '@confluence/feature-gating';
import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import {
	useCreatePersonalSpace,
	CreationSource,
} from '@confluence/onboarding-helpers/entry-points/hooks/useCreatePersonalSpace.tsx';

import { useSpaceEnabledContentTypes } from '../CreateContentPopover/useSpaceEnabledContentTypes.tsx';
import { ConfluencePageSubType } from '../queries/__types__/CreateContentMutation.ts';
import { buildCreationLink } from '../CreateContentRouteComponent/createContentRouteUtils.ts';

/**
 * Shared, narrow "Create doc" flow for the For You home empty states
 */

export type CreateDocStrategy = 'personalSpaceLiveDoc' | 'cwrLiveDoc' | 'creationLink';

type CreateDocContentType = 'liveDoc' | 'page';

export type CreateDocAction =
	| {
			strategy: 'personalSpaceLiveDoc';
			kind: 'link';
			subType: ConfluencePageSubType.LIVE;
			spaceKey: string;
			contentType: 'liveDoc';
	  }
	| {
			strategy: 'cwrLiveDoc';
			kind: 'cwr';
			contentType: 'liveDoc';
	  }
	| {
			strategy: 'creationLink';
			kind: 'link';
			subType: ConfluencePageSubType.LIVE | undefined;
			spaceKey: string | undefined;
			contentType: CreateDocContentType;
	  };

type ResolveCreateDocActionInputs = {
	/** True once both the personal-space lookup and its space-content-types lookup have resolved. */
	resolved: boolean;
	/** The user's personal space key, when one exists and has resolved. */
	personalSpaceKey: string | undefined;
	/** Whether the user has a personal space (resolved value). */
	hasPersonalSpace: boolean;
	/**
	 * Whether live docs are enabled for `personalSpaceKey`. When no personal space key is
	 * present this reflects the site-default capability (used only for the safe fallback).
	 */
	isCreateLiveDocEnabled: boolean;
	/** Whether Create With Rovo can open for this user/session. */
	canOpenCreateWithRovo: boolean;
};

/**
 * Decision function for the shared home empty-state "Create doc" CTA.
 *
 */
export const resolveCreateDocAction = ({
	resolved,
	personalSpaceKey,
	hasPersonalSpace,
	isCreateLiveDocEnabled,
	canOpenCreateWithRovo,
}: ResolveCreateDocActionInputs): CreateDocAction => {
	// Mirror the legacy useCreateDoc
	// (subType follows the site-default live-doc capability, no targeted space).
	if (!resolved) {
		return {
			strategy: 'creationLink',
			kind: 'link',
			subType: isCreateLiveDocEnabled ? ConfluencePageSubType.LIVE : undefined,
			spaceKey: undefined,
			contentType: isCreateLiveDocEnabled ? 'liveDoc' : 'page',
		};
	}

	// Personal space exists.
	if (hasPersonalSpace && personalSpaceKey) {
		// Branch 1: live docs enabled in the personal space.
		if (isCreateLiveDocEnabled) {
			return {
				strategy: 'personalSpaceLiveDoc',
				kind: 'link',
				subType: ConfluencePageSubType.LIVE,
				spaceKey: personalSpaceKey,
				contentType: 'liveDoc',
			};
		}
		// Branch 3: live docs disabled in the personal space -> plain page in that space.
		return {
			strategy: 'creationLink',
			kind: 'link',
			subType: undefined,
			spaceKey: personalSpaceKey,
			contentType: 'page',
		};
	}

	// Branch 2: no personal space -> CWR with live doc preselected.
	if (canOpenCreateWithRovo) {
		return { strategy: 'cwrLiveDoc', kind: 'cwr', contentType: 'liveDoc' };
	}

	// Branch 2 fallback: CWR unavailable -> existing creation-link behaviour with live-doc intent.
	return {
		strategy: 'creationLink',
		kind: 'link',
		subType: ConfluencePageSubType.LIVE,
		spaceKey: undefined,
		contentType: 'liveDoc',
	};
};

export type UseCreateHomeEmptyStateDocProps = {
	/** Stable analytics source for the surface using the CTA, e.g. 'popularFeedEmptyState'. */
	source: string;
};

export type UseCreateHomeEmptyStateDocResult = {
	createDoc: () => void;
};

export const useCreateHomeEmptyStateDoc = ({
	source,
}: UseCreateHomeEmptyStateDocProps): UseCreateHomeEmptyStateDocResult => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const { push } = useRouteActions();
	const { openModal } = useSmartCreationModalActions();
	const { isRovoLLMEnabled } = useSessionData();
	const isLicensed = useIsLicensed();

	const { personalSpaceKeyData, personalSpaceKeyLoading } = useCreatePersonalSpace({
		source: CreationSource.NTH_USER_ACCOUNT_SETUP,
	});

	const personalSpaceKey = personalSpaceKeyData?.user?.confluence?.space?.key ?? undefined;
	const hasPersonalSpace = Boolean(personalSpaceKeyData?.user?.confluence?.hasPersonalSpace);

	const { isCreateLiveDocEnabled, isLoading: spaceContentTypesLoading } =
		useSpaceEnabledContentTypes(personalSpaceKey);

	const createDoc = useCallback(() => {
		// Only gate on content types when a personal space key exists.
		const resolved = !personalSpaceKeyLoading && (!personalSpaceKey || !spaceContentTypesLoading);
		const hasUsablePersonalSpace = resolved && hasPersonalSpace && Boolean(personalSpaceKey);

		// The CWR availability gate only affects the outcome for a resolved user with no usable
		// personal space, so evaluate it only then.
		const canOpenCreateWithRovo =
			resolved &&
			!hasUsablePersonalSpace &&
			isRovoLLMEnabled &&
			isLicensed &&
			fg('aifc_create_enabled');

		const action = resolveCreateDocAction({
			resolved,
			personalSpaceKey,
			hasPersonalSpace,
			isCreateLiveDocEnabled,
			canOpenCreateWithRovo,
		});

		createEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createDoc',
				source,
				attributes: {
					createDocStrategy: action.strategy,
					hasPersonalSpace,
					contentType: action.contentType,
				},
			},
		}).fire();

		if (action.kind === 'cwr') {
			openModal({ entryPoint: source, spaceKey: '', initialOutcomeType: 'liveDoc' });
			return;
		}

		push(
			buildCreationLink({
				contentType: 'page',
				subType: action.subType,
				spaceKey: action.spaceKey,
				withFallback: true,
				source,
			}),
		);
	}, [
		createEvent,
		push,
		openModal,
		source,
		isRovoLLMEnabled,
		isLicensed,
		personalSpaceKey,
		hasPersonalSpace,
		isCreateLiveDocEnabled,
		personalSpaceKeyLoading,
		spaceContentTypesLoading,
	]);

	return { createDoc };
};
